/**
 * OnboardingRouter - Sequential page guards for onboarding flow.
 * Feature 016: Enforces page order based on pages_completed array.
 *
 * Sequential flow:
 * 1. /onboarding/personal-details (no prerequisites)
 * 2. /onboarding/goals (requires personal_details)
 * 3. /onboarding/body-state (requires personal_details + goals)
 * 4. /onboarding/struggles (requires personal_details + goals + body_state)
 */

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import PersonalDetailsPage from './PersonalDetailsPage';
import GoalsPage from './GoalsPage';
import BodyStatePage from './BodyStatePage';
import StrugglesPage from './StrugglesPage';

const OnboardingRouter: React.FC = () => {
  const { user } = useAuth();

  // Get pages completed from user onboarding status
  const pagesCompleted = user?.onboarding?.pages_completed || [];

  // T075-T078: Sequential page guards
  // Check if user has completed prerequisite pages
  const canAccessGoals = pagesCompleted.includes('personal_details');
  const canAccessBodyState = pagesCompleted.includes('personal_details') && pagesCompleted.includes('goals');
  const canAccessStruggles = canAccessBodyState && pagesCompleted.includes('body_state');

  return (
    <Routes>
      {/* Personal Details - Always accessible (no prerequisites) */}
      <Route path="/personal-details" element={<PersonalDetailsPage />} />

      {/* Goals - Requires personal_details completed */}
      <Route
        path="/goals"
        element={
          canAccessGoals ? (
            <GoalsPage />
          ) : (
            <Navigate to="/onboarding/personal-details" replace />
          )
        }
      />

      {/* Body State - Requires personal_details + goals completed */}
      <Route
        path="/body-state"
        element={
          canAccessBodyState ? (
            <BodyStatePage />
          ) : (
            <Navigate to="/onboarding/personal-details" replace />
          )
        }
      />

      {/* Struggles - Requires personal_details + goals + body_state completed */}
      <Route
        path="/struggles"
        element={
          canAccessStruggles ? (
            <StrugglesPage />
          ) : (
            <Navigate to="/onboarding/personal-details" replace />
          )
        }
      />

      {/* Default redirect to first page */}
      <Route path="*" element={<Navigate to="/onboarding/personal-details" replace />} />
    </Routes>
  );
};

export default OnboardingRouter;
